import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button, SafeImg } from '@/components/atoms';
import { ApiError } from '@/services/api';
import { boardApi, type BoardCard } from '@/services/board';
import { destinationsApi } from '@/services/config';
import {
  contentService,
  type GenerateResult,
  type SourceType,
  type WpSeoPayload,
} from '@/services/content';
import type { NewsCard, TopicDetail } from '@/services/intelligence';
import { formatNewsDate } from '@/utils/datetime';
import { destinoPadrao } from '@/config/escopo';

import styles from './FrammingModal.module.css';

/**
 * `detail` do backend chega como string OU como objeto {erro, motivo} — o 422 de
 * marca ausente (POST /board/from-intelligence) usa a segunda forma. A leitura
 * antiga assumia string e entregava o OBJETO ao JSX, o que derruba a arvore
 * ("Objects are not valid as a React child") em vez de explicar a recusa.
 */
function mensagemDeFalha(err: unknown): string {
  if (err instanceof ApiError) {
    const detalhe = (err.body as { detail?: unknown } | null)?.detail;
    if (typeof detalhe === 'string' && detalhe) return detalhe;
    if (detalhe && typeof detalhe === 'object') {
      const { erro, motivo } = detalhe as { erro?: string; motivo?: string };
      if (err.status === 422 && erro?.includes('marca')) {
        return 'Escolha o destino aqui — ou a marca no topo, na sessão da casa — antes de enviar para produção.';
      }
      if (erro) return motivo ? `${erro} — ${motivo}` : erro;
      if (motivo) return motivo;
    }
  }
  return 'Falha ao gerar. Tente novamente.';
}

export function FrammingModal({
  open,
  onClose,
  sourceType,
  tema,
  payload,
  intel,
  sourceOptions,
  marcaFallback,
  onGenerated,
  onProduced,
}: {
  open: boolean;
  onClose: () => void;
  sourceType: SourceType;
  tema: string;
  payload: Record<string, unknown>;
  /** Handoff curadoria→produção (cobertura/PAA) — vai junto quando envia pra produção. */
  intel?: TopicDetail;
  /** Framming de FONTES: matérias da cobertura pro produtor escolher (opcional).
   * Sem seleção → o motor escolhe as top-3 do cluster (comportamento padrão). */
  sourceOptions?: NewsCard[];
  /** Marca assumida pela sessao STAFF, usada quando nenhum destino foi escolhido.
   * Em tenant vem undefined e nada muda: a marca sai do client_id da sessao. */
  marcaFallback?: string;
  onGenerated?: (r: GenerateResult) => void;
  /** Disparado quando o card entra em "Produzindo" (com ângulo). */
  onProduced?: (card: BoardCard) => void;
}) {
  const [framming, setFramming] = useState('');
  const [dest, setDest] = useState('');
  const [imagemIa, setImagemIa] = useState(false);
  const [selLinks, setSelLinks] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<GenerateResult | null>(null);

  const selecionaveis = (sourceOptions ?? []).filter((n) => n.link);
  const toggleLink = (link: string) =>
    setSelLinks((prev) => {
      const next = new Set(prev);
      if (next.has(link)) next.delete(link);
      else next.add(link);
      return next;
    });

  const sitesQuery = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => destinationsApi.list(),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const sites = sitesQuery.data ?? [];
  const hasDest = Boolean(dest);
  // O handoff para o quadro exige marca na sessao da casa (422 do backend). O
  // rascunho direto no WP segue exigindo destino escolhido a mao (hasDest).
  const siteDoCard = dest || marcaFallback || undefined;

  // Default: ao carregar os sites, já seleciona o primeiro destino (evita o erro
  // "Nenhum destino configurado" por esquecer de escolher). O usuário pode trocar.
  useEffect(() => {
    // Destino de publicacao nao se escolhe sozinho quando ha mais de um.
    if (!dest) {
      const padrao = destinoPadrao(sites);
      if (padrao) setDest(padrao);
    }
  }, [sites, dest]);

  const run = useMutation({
    mutationFn: (vars: { dry_run: boolean; status?: 'draft' | 'publish' }) =>
      contentService.generate({
        source_type: sourceType,
        payload: {
          ...payload,
          tema,
          imagem_ia: imagemIa,
          ...(selLinks.size > 0 ? { source_links: [...selLinks] } : {}),
        },
        framming: framming.trim(),
        status: vars.status ?? 'draft',
        wp_site: dest || undefined,
        dry_run: vars.dry_run,
      }),
    onSuccess: (r) => {
      setResult(r);
      onGenerated?.(r);
    },
  });

  // Enviar para PRODUÇÃO já produzindo com o ângulo: cria o card (handoff) e
  // dispara o agente (metodo 'framing') → entra direto na raia "Produzindo".
  // Destino é opcional aqui (quem produzir pode escolher depois), ao contrário
  // do rascunho direto no WP.
  const enviarProducao = useMutation({
    mutationFn: async () => {
      const card = await boardApi.enqueue({
        tema,
        fonte: 'inteligencia',
        site: siteDoCard,
        editoria: typeof payload.editoria === 'string' ? payload.editoria : intel?.editoria ?? undefined,
        // B-13: handoff por referência (snapshot). Fallback inline se não houver intel_id.
        intel_id: intel?.intel_id ?? undefined,
        intel: intel && !intel.intel_id ? intel : undefined,
      });
      return boardApi.produzir(card.id, {
        metodo: 'framing',
        framming: framming.trim(),
        // A mesma marca do card: criar o card numa marca e produzir noutra (ou
        // em nenhuma) era o caminho para o texto sair no lugar errado.
        site: siteDoCard,
        source_links: selLinks.size > 0 ? [...selLinks] : undefined,
      });
    },
    onSuccess: (card) => {
      onProduced?.(card);
      setFramming('');
      setDest('');
      setImagemIa(false);
      setSelLinks(new Set());
      setResult(null);
      run.reset();
      onClose();
    },
  });

  // Teste humano 03/09: "Pré-visualizar" (dry_run) saiu da UI — o produtor
  // não usava; a prévia promovida (POST /content/save-preview) saiu junto.

  const activeErr = run.error ?? enviarProducao.error;
  const errMsg = activeErr ? mensagemDeFalha(activeErr) : '';

  if (!open) return null;

  const close = () => {
    setFramming('');
    setDest('');
    setImagemIa(false);
    setSelLinks(new Set());
    setResult(null);
    run.reset();
    onClose();
  };

  return (
    <div className={styles.backdrop} onClick={close}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <header className={styles.head}>
          <div>
            <h2 className={styles.title}>Gerar com ângulo</h2>
            <p className={styles.tema}>{tema}</p>
          </div>
          <button className={styles.x} onClick={close} aria-label="Fechar">×</button>
        </header>

        <label className={styles.label}>
          Framming (ângulo editorial) — opcional
          <textarea
            className={styles.textarea}
            value={framming}
            onChange={(e) => setFramming(e.target.value)}
            placeholder="Ex.: foque no impacto para o bolso do trabalhador; tom analítico; conecte ao cenário local…"
            rows={3}
          />
          <span className={styles.hint}>
            Vazio = matéria neutra. Preenchido = a IA escreve sob essa ótica,
            mantendo a factualidade.
          </span>
        </label>

        {selecionaveis.length > 0 && (
          <div className={styles.label}>
            Matérias-fonte — opcional ({selLinks.size > 0 ? `${selLinks.size} escolhida${selLinks.size > 1 ? 's' : ''}` : 'automático'})
            <div
              style={{
                maxHeight: 210, overflowY: 'auto', marginTop: 6,
                border: '1px solid var(--border-1)', borderRadius: 6,
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th style={{ position: 'sticky', top: 0, background: 'var(--bg-1)', textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: 'var(--text-3)' }}>Matéria</th>
                    <th style={{ position: 'sticky', top: 0, background: 'var(--bg-1)', textAlign: 'right', padding: '6px 8px', fontWeight: 500, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>Publicado</th>
                  </tr>
                </thead>
                <tbody>
                  {selecionaveis.map((n) => {
                    const on = selLinks.has(n.link!);
                    return (
                      <tr
                        key={n.link!}
                        onClick={() => toggleLink(n.link!)}
                        style={{ cursor: 'pointer', background: on ? 'var(--bg-2)' : 'transparent', borderTop: '1px solid var(--border-1)' }}
                      >
                        <td style={{ padding: '6px 8px' }}>
                          <input type="checkbox" checked={on} readOnly style={{ marginRight: 8, verticalAlign: 'middle' }} />
                          {n.source && <b style={{ marginRight: 4 }}>{n.source}</b>}
                          {n.title}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--text-3)' }}>
                          {n.date ? formatNewsDate(n.date) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <span className={styles.hint}>
              Um trending traz vários assuntos (ex.: &quot;Trump&quot; com 3–4 fatos distintos).
              Marque as matérias do assunto que você quer — a IA usa <b>só elas</b> como fonte.
              Sem marcação, o motor escolhe as top-3 do cluster.
            </span>
          </div>
        )}

        <label className={styles.label}>
          Destino (site WordPress)
          {sites.length > 0 ? (
            <select
              className={styles.select}
              value={dest}
              onChange={(e) => setDest(e.target.value)}
            >
              <option value="">— escolha o destino —</option>
              {sites.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : (
            <span className={styles.warn}>
              {sitesQuery.isLoading
                ? 'Carregando os sites da marca…'
                : <>Nenhum destino configurado. Cadastre um site em <b>Config → Sites</b> para
                  gerar rascunho direto no WP. (<b>Enviar para produção</b> funciona sem
                  destino — o produtor escolhe depois.)</>}
            </span>
          )}
        </label>

        <label className={styles.checkRow}>
          <input
            type="checkbox"
            checked={imagemIa}
            onChange={(e) => setImagemIa(e.target.checked)}
          />
          <span>
            Gerar capa por IA se a fonte não tiver imagem
            <small className={styles.hint}> (custa ~US$0,04; por padrão usa a foto da fonte)</small>
          </span>
        </label>

        {!hasDest && !sitesQuery.isLoading && (
          // Antes o motivo só aparecia no tooltip do botão cinza (teste 03/09:
          // "salvar rascunho não dá para clicar"). Agora está escrito.
          <p className={styles.hint}>
            <b>Salvar rascunho</b> precisa de um destino — escolha o site acima.
          </p>
        )}
        <div className={styles.actions}>
          <Button
            variant="secondary"
            onClick={() => run.mutate({ dry_run: false, status: 'draft' })}
            loading={run.isPending}
            disabled={!hasDest}
            title={hasDest ? 'Gera e salva rascunho direto no WordPress' : 'Escolha um destino primeiro'}
          >
            Salvar rascunho
          </Button>
          <Button
            onClick={() => enviarProducao.mutate()}
            loading={enviarProducao.isPending}
            title="Cria o card e já começa a produzir com este ângulo (entra em Produzindo)"
          >
            Enviar para produção
          </Button>
        </div>

        {errMsg && <p className={styles.err}>{errMsg}</p>}

        {result && (
          <div className={styles.result}>
            <div className={styles.resultHead}>
              <span className={styles.badge}>
                {result.status === 'publish' ? 'PUBLICADO' : 'RASCUNHO'}
              </span>
              <strong>{result.title}</strong>
              <span className={styles.sources}>
                {result.num_sources} fontes
                {typeof result.scraped === 'number' ? ` · ${result.scraped} com texto` : ''}
              </span>
            </div>
            {result.cover_image_url && (
              <div className={styles.coverRow}>
                <SafeImg
                  className={styles.coverThumb}
                  placeholderClassName={styles.coverCap}
                  src={result.cover_image_url}
                  site={dest || undefined}
                  alt="capa"
                />
                <span className={styles.coverCap}>
                  capa da fonte-âncora{result.cover_source ? ` · ${result.cover_source}` : ''}
                </span>
              </div>
            )}
            {result.excerpt && <p className={styles.excerpt}>{result.excerpt}</p>}
            {result.seo && <SeoSummary seo={result.seo} />}
            {result.wp_link && (
              <a className={styles.link} href={result.wp_link} target="_blank" rel="noreferrer">
                abrir no WordPress #{result.wp_id}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SeoSummary({ seo }: { seo: WpSeoPayload }) {
  const faqCount = Array.isArray((seo.schema.faq as { mainEntity?: unknown[] } | null)?.mainEntity)
    ? ((seo.schema.faq as { mainEntity: unknown[] }).mainEntity.length)
    : 0;
  return (
    <div className={styles.seoBox}>
      <div className={styles.seoHead}>
        <span className={styles.seoTitle}>SEO / Schema — pronto pro WP</span>
        <span className={seo.wordcount_ok ? styles.okBadge : styles.warnBadge}>
          {seo.word_count} palavras
        </span>
      </div>

      <div className={styles.seoGrid}>
        <SeoField label="Chapéu">{seo.chapeu || '—'}</SeoField>
        <SeoField label="Linha fina">{seo.linha_fina || '—'}</SeoField>
        <SeoField label="Editoria">
          {seo.editoria.name || '—'}
          {seo.editoria.id != null && <small> · #{seo.editoria.id}</small>}
        </SeoField>
        <SeoField label="Autor">
          {seo.autor.name || '—'}
          {seo.autor.id != null && <small> · #{seo.autor.id}</small>}
        </SeoField>
        <SeoField label="Créditos da imagem">
          {seo.image.credits || '— (não encontrados)'}
        </SeoField>
        <SeoField label="Seção">{seo.secao.name}</SeoField>
      </div>

      {seo.keywords.length > 0 && (
        <div className={styles.seoKw}>
          <span className={styles.seoLabel}>Palavras-chave → tags</span>
          <div className={styles.chips}>
            {seo.keywords.map((k) => (
              <span key={k} className={styles.chip}>{k}</span>
            ))}
          </div>
        </div>
      )}

      {seo.meta && Object.keys(seo.meta).length > 0 && (
        <div className={styles.seoKw}>
          <span className={styles.seoLabel}>Campos custom → WP (meta)</span>
          {Object.entries(seo.meta).map(([k, v]) => (
            <SeoField key={k} label={k}>{v || '—'}</SeoField>
          ))}
        </div>
      )}

      <details className={styles.schemaDetails}>
        <summary>NewsArticle JSON-LD (compliance Google)</summary>
        <pre className={styles.schemaPre}>
          {JSON.stringify(seo.schema.news_article, null, 2)}
        </pre>
      </details>
      {seo.schema.faq && (
        <details className={styles.schemaDetails}>
          <summary>FAQPage JSON-LD ({faqCount} perguntas)</summary>
          <pre className={styles.schemaPre}>{JSON.stringify(seo.schema.faq, null, 2)}</pre>
        </details>
      )}
      <p className={styles.seoNote}>
        O JSON-LD vai embutido no conteúdo (testável no Rich Results Test). Mapear
        chapéu/linha-fina/schema nos campos do tema fica a cargo do admin (config por WP).
      </p>
    </div>
  );
}

function SeoField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.seoField}>
      <span className={styles.seoLabel}>{label}</span>
      <span className={styles.seoValue}>{children}</span>
    </div>
  );
}
